// src/portal-app/team/RolePermissionEditor.js
//
// The permission grid for one role — fetch, category grouping, the
// view/edit auto-logic (uncheck view -> auto-uncheck edit; check edit ->
// auto-check view), dirty tracking, save and reset.
//
// Extracted from RolesScreen.js in the Plan 4 Task 20 commit (b61bf97) — see
// rolesShared.js for why the file was split, and RolesScreen.js's
// "FILE SPLIT" header note for why this went into the same commit as the
// P4 migration rather than a separate extraction commit first. Isolated
// into its own module deliberately: this screen is the permission editor,
// so THIS component is the single most permission-sensitive piece on it.
// This module IS a verbatim move — the only change from the pre-split
// function is `function PermissionEditor` -> `export function
// RolePermissionEditor` (name + export keyword). Logic, field names
// (permission_key, can_view, can_edit, category, label — verified against
// backend/app/services/permissions.py:get_permissions_for_role and
// backend/app/models/models.py PermissionEntry/PermissionBulkUpdate) and
// every testid (perm-view-{key}, perm-edit-{key}, perm-dirty-bar,
// perm-reset-btn, perm-save-btn) are byte-identical to the pre-split
// screen — diff this file's body against
// `git show b61bf97^:frontend/src/portal-app/team/RolesScreen.js` (the
// PermissionEditor function there) to verify. This is not a P4
// CondensedRow surface (spec §5 has no row contract for a two-column
// checkbox grid), so it is carried over verbatim
// rather than reshaped.
import React, {
    useState, useEffect, useCallback, useRef,
} from 'react';
import { roleService } from '../../services/api';
import { GlassCard } from '../shell/LiquidGlass';

export function RolePermissionEditor({ roleId, tenantId, t }) {
    const [perms, setPerms] = useState([]);
    const [original, setOriginal] = useState([]);
    const [loadStatus, setLoadStatus] = useState('loading');
    const [saving, setSaving] = useState(false);
    const fetchRef = useRef(0);

    const load = useCallback(async () => {
        const id = ++fetchRef.current;
        setLoadStatus('loading');
        try {
            const data = await roleService.getRolePermissions(roleId, tenantId);
            if (id !== fetchRef.current) return;
            const arr = Array.isArray(data) ? data : [];
            setPerms(arr.map((p) => ({ ...p })));
            setOriginal(arr.map((p) => ({ ...p })));
            setLoadStatus('ready');
        } catch {
            if (id !== fetchRef.current) return;
            setLoadStatus('error');
        }
    }, [roleId, tenantId]);

    useEffect(() => { load(); }, [load]);

    // Group by category
    const categories = perms.reduce((acc, p) => {
        const cat = p.category || 'Other';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(p);
        return acc;
    }, {});

    // Dirty check
    const isDirty = perms.some((p, i) => {
        const o = original[i];
        return !o || p.can_view !== o.can_view || p.can_edit !== o.can_edit;
    });

    const toggleView = (key) => {
        setPerms((prev) => prev.map((p) => {
            if (p.permission_key !== key) return p;
            const newView = !p.can_view;
            // uncheck view → also uncheck edit
            return { ...p, can_view: newView, can_edit: newView ? p.can_edit : false };
        }));
    };

    const toggleEdit = (key) => {
        setPerms((prev) => prev.map((p) => {
            if (p.permission_key !== key) return p;
            const newEdit = !p.can_edit;
            // check edit → also check view
            return { ...p, can_edit: newEdit, can_view: newEdit ? true : p.can_view };
        }));
    };

    const handleReset = () => {
        setPerms(original.map((p) => ({ ...p })));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await roleService.updateRolePermissions(roleId, tenantId, perms.map((p) => ({
                permission_key: p.permission_key,
                can_view: p.can_view,
                can_edit: p.can_edit,
            })));
            setOriginal(perms.map((p) => ({ ...p })));
        } catch {
            // Keep dirty state so user can retry
        }
        setSaving(false);
    };

    if (loadStatus === 'loading') {
        return (
            <GlassCard veil className="rl-perm-editor">
                <div className="rl-perm-header">
                    <div className="rl-perm-title" style={{ opacity: 0.5 }}>
                        {t('permissions', {}, 'Permissions')}
                    </div>
                </div>
            </GlassCard>
        );
    }

    if (loadStatus === 'error') {
        return (
            <GlassCard veil className="rl-perm-editor">
                <div className="rl-perm-header">
                    <div className="rl-perm-title" style={{ color: 'var(--c-red)' }}>
                        {t('permissions_load_error', {}, 'Failed to load permissions')}
                    </div>
                </div>
            </GlassCard>
        );
    }

    return (
        <>
            <GlassCard veil className="rl-perm-editor">
                <div className="rl-perm-header">
                    <div className="rl-perm-title">{t('permissions', {}, 'Permissions')}</div>
                    {/* Column headers */}
                    <div style={{ display: 'flex', gap: 16, paddingInlineEnd: 4 }}>
                        <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: '.6px', color: 'var(--c-textLight)', minWidth: 32, textAlign: 'center' }}>
                            {t('view', {}, 'View')}
                        </span>
                        <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: '.6px', color: 'var(--c-textLight)', minWidth: 32, textAlign: 'center' }}>
                            {t('edit', {}, 'Edit')}
                        </span>
                    </div>
                </div>

                {Object.entries(categories).map(([cat, catPerms]) => (
                    <div key={cat} className="rl-perm-category">
                        <div className="rl-perm-cat-label">{cat}</div>
                        {catPerms.map((p) => (
                            <div key={p.permission_key} className="rl-perm-row">
                                <span className="rl-perm-label">{p.label}</span>
                                <div className="rl-perm-toggles">
                                    <div className="rl-perm-toggle-col">
                                        <input
                                            type="checkbox"
                                            className="rl-checkbox"
                                            data-testid={`perm-view-${p.permission_key}`}
                                            checked={!!p.can_view}
                                            onChange={() => toggleView(p.permission_key)}
                                            aria-label={`${p.label} view`}
                                        />
                                    </div>
                                    <div className="rl-perm-toggle-col">
                                        <input
                                            type="checkbox"
                                            className="rl-checkbox"
                                            data-testid={`perm-edit-${p.permission_key}`}
                                            checked={!!p.can_edit}
                                            onChange={() => toggleEdit(p.permission_key)}
                                            aria-label={`${p.label} edit`}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </GlassCard>

            {/* Dirty bar */}
            {isDirty && (
                <div className="rl-dirty-bar" data-testid="perm-dirty-bar">
                    <span className="rl-dirty-msg">
                        {t('unsaved_permission_changes', {}, 'Unsaved changes')}
                    </span>
                    <button
                        type="button"
                        className="rl-dirty-reset"
                        data-testid="perm-reset-btn"
                        onClick={handleReset}
                    >
                        {t('reset', {}, 'Reset')}
                    </button>
                    <button
                        type="button"
                        className="rl-dirty-save"
                        data-testid="perm-save-btn"
                        disabled={saving}
                        onClick={handleSave}
                    >
                        {saving && <span className="rl-spinner" aria-hidden="true" />}
                        {t('save', {}, 'Save')}
                    </button>
                </div>
            )}
        </>
    );
}

export default RolePermissionEditor;
